<template>
  <div class="card-list-data" v-if="!selectedServiceDetail">
    <!-- category + search stay pinned while the service list scrolls under them -->
    <div class="service-filters" v-if="!IS_LOADER && Object.keys(serviceList).length > 0">
    <div class="category-filter-wrapper mb-3">
      <div class="category-scroll-container pb-2">
        <!-- The "All" card is gone: it was the default state anyway, and on a phone
             it cost one of the four category slots that actually fit on screen.
             Tapping the active category now clears the filter, so "all services"
             is still reachable without a dedicated button. -->
        <div v-for="category in Object.values(serviceList)" :key="category.category_id"
          class="category-card-item" :class="{ active: selectedCategoryId == category.category_id }"
          @click="selectedCategoryId = selectedCategoryId == category.category_id ? null : category.category_id">
          <div class="category-card-image-box">
            <img v-if="category.category_image" :src="category.category_image" class="category-card-img" :alt="category.category_name">
            <div v-else class="category-placeholder-box">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="opacity-25">
                <path d="M4 16L8.586 11.414C8.961 11.039 9.47 10.828 10 10.828C10.53 10.828 11.039 11.039 11.414 11.414L16 16M14 14L15.586 12.414C15.961 12.039 16.47 11.828 17 11.828C17.53 11.828 18.039 12.039 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <span class="category-card-name text-truncate">{{ categoryLabel(category.category_name) }}</span>
        </div>
      </div>
    </div>
    <div class="search-wrapper mb-3">
      <div class="search-input-wrapper position-relative">
        <div class="search-icon-container position-absolute top-50 start-0 translate-middle-y ps-2 ms-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#6c757d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <input type="text" v-model="searchQuery" class="form-control search-field border-0 py-3 ps-5 bg-soft-light rounded-pill shadow-none"
          style="padding-left: 3rem !important;"
          :placeholder="$t('quick_booking.lbl_search_services') || 'Search services...'" />
      </div>
    </div>
    </div>
    <div class="qb-staff-filter" v-if="!IS_LOADER && staffFilterActive">
      <span>Услугите на <b>{{ pre.employee_name }}</b></span>
      <button type="button" class="qb-staff-filter-all" @click="staffOnly = false">Покажи всички</button>
    </div>
    <div class="row g-2 mb-3" v-if="!IS_LOADER">
      <template v-for="(serviceItem, index) in finalFilteredServices" :key="`services-${index}`">
        <div class="col-12">
          <div class="iq-widget h-100">
            <input type="checkbox" :id="serviceItem.name + serviceItem.id" v-model="selectedServiceIds" :value="serviceItem.id"
              class="btn-check" />
            <label :for="serviceItem.name + serviceItem.id" class="d-block h-100">
              <div class="card iq-service-box h-100 overflow-hidden border-0">
                <div class="d-flex align-items-center h-100">
                  <div class="service-image-wrapper">
                    <img v-if="serviceItem.attachement_full || serviceItem.feature_image || serviceItem.service_image || serviceItem.image"
                      :src="serviceItem.attachement_full || serviceItem.feature_image || serviceItem.service_image || serviceItem.image"
                      class="service-img" alt="service">
                    <div v-else class="service-image-placeholder">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="opacity-25">
                        <path d="M4 16L8.586 11.414C8.961 11.039 9.47 10.828 10 10.828C10.53 10.828 11.039 11.039 11.414 11.414L16 16M14 14L15.586 12.414C15.961 12.039 16.47 11.828 17 11.828C17.53 11.828 18.039 12.039 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <!-- The bootstrap flex utilities are !important, which blocks the
                         phone layout from re-grouping this row with a grid, so the
                         row does its own layout in the stylesheet below. -->
                    <div class="card-body service-row py-1 ps-1 pe-2">
                      <div class="service-main pe-2">
                        <h6 class="service-name mb-0">{{ serviceItem.name }}</h6>
                        <!-- Duration and the details link share one line: the card used
                             to spend three text rows per procedure, so barely two fitted
                             on a phone screen. -->
                        <div class="service-sub">
                          <span class="service-duration-text">{{ serviceItem.duration_min }} {{ $t('quick_booking.lbl_min') || 'min' }}</span>
                          <button type="button" class="btn-details-link-mobile" @click.stop.prevent="openDetails(serviceItem)">
                            {{ $t('quick_booking.lbl_see_details') || 'See Details' }}
                          </button>
                        </div>
                      </div>
                      <div class="service-meta-side">
                        <div class="selection-marker"></div>
                        <div class="service-price-primary fw-bold">
                          {{ formatCurrencyVue(serviceItem.default_price) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>
      </template>
    </div>
    <div class="loading-services-wrapper" v-if="IS_LOADER">
      <!-- Category Skeleton (Pills) -->
      <div class="d-flex gap-2 mb-4 overflow-hidden">
        <div class="skeleton rounded-pill py-3 px-4 w-25"></div>
        <div class="skeleton rounded-pill py-3 px-4 w-25"></div>
        <div class="skeleton rounded-pill py-3 px-4 w-25"></div>
        <div class="skeleton rounded-pill py-3 px-4 w-25"></div>
      </div>
      <!-- Search Skeleton -->
      <div class="skeleton rounded-pill p-3 mb-4 w-100"></div>
      <!-- List Skeletons -->
      <div class="row g-3">
        <div class="col-12" v-for="index in 6" :key="index">
          <div class="card card-skeleton border-0 overflow-hidden shadow-none mb-0">
            <div class="card-body py-3 ps-2 pe-3 d-flex align-items-center">
              <div class="skeleton skeleton-image-placeholder me-2"></div>
              <div class="flex-grow-1 pe-2">
                <div class="skeleton skeleton-line mb-2 w-75"></div>
                <div class="skeleton skeleton-line mb-0 w-50"></div>
              </div>
              <div class="service-meta-side text-end" style="min-width: 80px;">
                <div class="skeleton skeleton-circle mb-2 ms-auto"></div>
                <div class="skeleton skeleton-line w-100"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="Object.keys(serviceList).length == 0 && !IS_LOADER"
      class="h-100 w-75 d-flex align-items-center justify-content-center">
      {{ $t('quick_booking.msg_no_services_available') || 'We apologize for any inconvenience caused. Unfortunately, the selected salon branch does not offer the service you are looking for at the moment.' }}
    </div>
  </div>

  <!-- Detail View -->
  <div class="service-detail-view" v-else>
    <div class="detail-header mb-4">
      <button type="button" class="btn-back-to-list" @click="closeDetails">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="me-2">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ $t('quick_booking.lbl_back_to_services') || 'Back to Services' }}
      </button>
    </div>
    <div class="detail-content card border-0 shadow-sm overflow-hidden p-0 rounded-4">
      <!-- 1. Image -->
      <div class="detail-image-wrapper" v-if="selectedServiceDetail.attachement_full || selectedServiceDetail.feature_image || selectedServiceDetail.service_image || selectedServiceDetail.image">
        <img :src="selectedServiceDetail.attachement_full || selectedServiceDetail.feature_image || selectedServiceDetail.service_image || selectedServiceDetail.image" class="img-fluid w-100 detail-hero-img" alt="service">
      </div>

      <div class="detail-body p-4">
        <!-- 2. Title + Price -->
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h3 class="fw-bold text-dark mb-0">{{ selectedServiceDetail.name }}</h3>
          <div class="service-price-primary fs-4 fw-bold">
            {{ formatCurrencyVue(selectedServiceDetail.default_price) }}
          </div>
        </div>

        <!-- 3. Duration + Category badge -->
        <div class="d-flex flex-wrap gap-2 mb-4">
          <div class="detail-badge py-2 px-3">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="me-1">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
              <path d="M12 7V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            {{ selectedServiceDetail.duration_min }} {{ $t('quick_booking.lbl_min') || 'min' }}
          </div>
          <div class="detail-badge py-2 px-3" v-if="selectedServiceDetail.category_name">
            {{ selectedServiceDetail.category_name }}
          </div>
        </div>

        <!-- 4. Description -->
        <div class="detail-description mb-4" v-if="selectedServiceDetail.description">
          <h6 class="fw-bold mb-2">{{ $t('quick_booking.lbl_description') || 'Description' }}</h6>
          <p class="text-muted lh-lg mb-0 text-break">{{ selectedServiceDetail.description }}</p>
        </div>

        <!-- 5. Book button -->
        <button type="button" class="btn btn-primary-theme w-100 py-3 rounded-3 fw-bold shadow-sm" @click="toggleFromDetail(selectedServiceDetail)">
          {{ isSelected(selectedServiceDetail.id) ? ($t('quick_booking.lbl_remove_selection') || 'Remove from Selection') : ($t('quick_booking.lbl_book_now') || 'Book Now') }}
        </button>
      </div>
    </div>
  </div>

  <div class="selected-summary-bar mb-3" v-if="!selectedServiceDetail && selectedServiceIds.length > 0">
    <div class="d-flex flex-wrap gap-1 mb-2">
      <span class="selected-chip" v-for="svc in selectedServicesDetails" :key="svc.id">
        {{ svc.name }}
        <button type="button" class="chip-remove" @click="removeService(svc.id)">&times;</button>
      </span>
    </div>
    <div class="d-flex justify-content-between fw-bold">
      <span>{{ selectedServiceIds.length }} {{ $t('quick_booking.lbl_selected') || 'selected' }} &middot; {{ totalDurationMin }} {{ $t('quick_booking.lbl_min') || 'min' }}</span>
      <span class="text-primary">{{ formatCurrencyVue(totalPrice) }}</span>
    </div>
  </div>

  <div class="card-footer" v-if="!selectedServiceDetail">
    <button type="button" class="btn btn-secondary iq-text-uppercase" v-if="wizardPrev"
      @click="prevTabChange(wizardPrev)">{{ $t('quick_booking.lbl_back') }}</button>
    <button type="button" v-if="wizardNext" class="btn btn-primary iq-text-uppercase"
      :disabled="selectedServiceIds.length === 0" @click="nextTabChange(wizardNext)">{{ $t('quick_booking.lbl_next')
      }}</button>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import { useRequest } from '@/helpers/hooks/useCrudOpration'

// Select Options List Request
import { SERVICE_LIST } from '@/vue/constants/quick_booking'
import { useQuickBooking } from '../../store/quick-booking'
const props = defineProps({
  wizardNext: {
    default: '',
    type: [String, Number]
  },
  wizardPrev: {
    default: '',
    type: [String, Number]
  }
})
const emit = defineEmits(['tab-change', 'onReset'])
const formatCurrencyVue = (value) => {
  if (window.currencyFormat !== undefined && value) {
    return window.currencyFormat(value)
  }
  return value
}
const store = useQuickBooking()
const { listingRequest } = useRequest()

// State Declarations
const IS_LOADER = ref(true)
const serviceList = ref({})
const selectedServiceIds = ref([])
const selectedServiceDetail = ref(null)
const selectedCategoryId = ref(store.booking.category_id || null)
const searchQuery = ref('')

// Booking links from the public website (030), checked server-side into window.QB_PRESELECT.
// ?service=… selects that service once and moves straight on to the specialists;
// ?staff=… narrows the list to that person's services until "Покажи всички" is tapped.
const pre = (typeof window !== 'undefined' && window.QB_PRESELECT) || {}
const staffServiceIds = (pre.employee_service_ids || []).map(Number)
const staffOnly = ref(staffServiceIds.length > 0)
let preApplied = false
// Category names as customers read them ("Фризьор" → "Коса"), from config/public_site.php.
const categoryLabel = (name) => ((typeof window !== 'undefined' && window.QB_CATEGORY_NAMES) || {})[name] || name
const applyPreselect = () => {
  if (preApplied || !pre.service_id) return
  const id = Number(pre.service_id)
  if (!flattenedServiceList.value.some((service) => service.id === id)) return
  preApplied = true
  selectedServiceIds.value = [id]
  nextTick(() => emit('tab-change', props.wizardNext))
}

// Computed property to flatten services from all categories
const flattenedServiceList = computed(() => {
  return Object.values(serviceList.value).flatMap((category) => category.services || [])
})

const filteredServices = computed(() => {
  if (!selectedCategoryId.value) {
    return flattenedServiceList.value
  }
  return flattenedServiceList.value.filter((service) => service.category_id == selectedCategoryId.value)
})

// Only while the person actually has services in this salon's list; otherwise show all.
const staffFilterActive = computed(() =>
  staffOnly.value && flattenedServiceList.value.some((service) => staffServiceIds.includes(Number(service.id)))
)

const finalFilteredServices = computed(() => {
  let services = flattenedServiceList.value

  if (staffFilterActive.value) {
    services = services.filter((service) => staffServiceIds.includes(Number(service.id)))
  }

  if (selectedCategoryId.value) {
    services = services.filter((service) => service.category_id == selectedCategoryId.value)
  }

  if (searchQuery.value.trim() !== '') {
    const query = searchQuery.value.toLowerCase()
    services = services.filter((service) =>
      service.name && service.name.toLowerCase().includes(query)
    )
  }

  return services
})

const selectedServicesDetails = computed(() => {
  return selectedServiceIds.value
    .map((id) => flattenedServiceList.value.find((service) => service.id === id))
    .filter(Boolean)
})
const totalDurationMin = computed(() => selectedServicesDetails.value.reduce((sum, s) => sum + (Number(s.duration_min) || 0), 0))
const totalPrice = computed(() => selectedServicesDetails.value.reduce((sum, s) => sum + (Number(s.default_price) || 0), 0))
const groupData = (data) => {
  const result = {};
  data.forEach((obj) => {
    let key = obj.category_id
    if (!result[key]) {
      result[key] = {
        category_id: obj.category_id,
        category_name: obj.category_name,
        category_image: obj.category_image || obj.category_image_url || null, // Capture category image
        services: [],
      }
    }
    result[key].services.push({ ...obj })
  })
  return result
}
const getBranch = () => {
  IS_LOADER.value = true
  const data = {
    branch_id: store.booking.branch_id,
    category_id: null
  }
  if (store.booking.branch_id !== null) {
    listingRequest({ url: SERVICE_LIST, data: data }).then((res) => {
      IS_LOADER.value = false
      serviceList.value = groupData(res.data)
      applyPreselect()
    })
  }
}
watch(
  () => store.booking.branch_id,
  () => {
    getBranch()
  },
  { immediate: true }
)
onMounted(() => {
  if (Array.isArray(store.booking.services)) {
    selectedServiceIds.value = store.booking.services
      .map((service) => service.service_id)
      .filter((id) => id !== null && id !== undefined)
  }
})
watch(
  selectedServiceIds,
  (ids) => {
    const services = ids
      .map((id) => flattenedServiceList.value.find((service) => service.id === id))
      .filter(Boolean)
      .map((foundService) => ({
        duration_min: foundService.duration_min,
        employee_id: null,
        service_id: foundService.id,
        service_price: foundService.default_price,
        start_date_time: null
      }))
    store.updateBookingValues({ key: 'services', value: services })
  },
  { deep: true }
)
watch(
  () => store.bookingResponse,
  (value) => {
    resetData()
  },
  { deep: true }
)

const nextTabChange = (val) => emit('tab-change', val)
const prevTabChange = (val) => {
  resetData()
  emit('tab-change', val)
}
const resetData = () => {
  store.updateBookingValues({ key: 'services', value: [] })
  selectedServiceIds.value = []
  selectedServiceDetail.value = null
  searchQuery.value = ''
}

const openDetails = (service) => {
  selectedServiceDetail.value = service
}

const closeDetails = () => {
  selectedServiceDetail.value = null
}

const isSelected = (id) => selectedServiceIds.value.includes(id)

const toggleFromDetail = (service) => {
  const idx = selectedServiceIds.value.indexOf(service.id)
  if (idx === -1) {
    selectedServiceIds.value.push(service.id)
  } else {
    selectedServiceIds.value.splice(idx, 1)
  }
  closeDetails()
}

const removeService = (id) => {
  const idx = selectedServiceIds.value.indexOf(id)
  if (idx !== -1) {
    selectedServiceIds.value.splice(idx, 1)
  }
}
</script>

<style scoped>
.service-filters {
  position: sticky;
  top: 0;
  z-index: 3;
  background-color: #ffffff;
  padding-top: 2px;
}

.service-filters::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -12px;
  height: 12px;
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.card-list-data {
  position: relative;
  padding-top: 10px;
  padding-right: 10px;
}

.iq-widget {
  height: 100%;
}

.iq-widget label {
  height: 100%;
}

.card.iq-service-box {
  background-color: #ffffff;
  border: 1.5px solid #edeff2 !important;
  border-radius: 14px !important;
  cursor: pointer;
  transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: 0 1px 2px rgba(16, 18, 43, 0.04);
  /* The backend stylesheet gives every .card a 2rem bottom margin. Inside the
     h-100 chain (col > widget > label > card) that margin inflated the column,
     and the card then grew to 100% of the inflated column: 32px of empty
     space per procedure. */
  margin-bottom: 0 !important;
}

.iq-service-box:hover {
  border-color: rgba(var(--bs-primary-rgb), 0.45) !important;
  box-shadow: 0 12px 24px -14px rgba(16, 18, 43, 0.3);
  transform: translateY(-2px);
}

.service-image-wrapper {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  margin: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.service-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
}

.service-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card.iq-service-box:hover .service-img {
  transform: scale(1.1);
}

/* without min-width:0 the middle column refuses to shrink and the price
   column is pushed outside the card on narrow screens */
.iq-service-box .flex-grow-1 {
  min-width: 0;
}

/* Was clamped to two lines with an ellipsis, which cut the longer procedure
   names in half — you could not tell two similar treatments apart. The name is
   the whole point of the row, so it wraps to as many lines as it needs and the
   card grows with it. */
.service-name {
  overflow-wrap: break-word;
  font-size: 14px;
  font-weight: 600;
  color: #16182b;
  line-height: 1.3;
}

.service-sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 10px;
  margin-top: 2px;
}

.service-duration-text {
  font-size: 11px;
  color: #8c92a4;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-price-primary {
  font-size: 15px;
  color: var(--bs-primary);
  font-weight: 700;
  white-space: nowrap;
}

.btn-details-link-mobile {
  background: none;
  border: none;
  color: var(--bs-primary);
  font-size: 11px;
  font-weight: 600;
  padding: 0;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-details-link-mobile:hover {
  text-decoration: underline;
  opacity: 0.8;
}

.service-row {
  display: flex;
  align-items: center;
}

.service-main {
  flex: 1 1 auto;
  min-width: 0;
}

.service-meta-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  padding-left: 8px;
  min-width: 72px;
}

.selection-marker {
  width: 20px;
  height: 20px;
  border: 2px solid #edeff2;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease-in-out;
}

.iq-widget .btn-check:checked + label .iq-service-box {
  border-color: var(--bs-primary) !important;
  background-color: rgba(var(--bs-primary-rgb), 0.05);
  box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.14);
}

.iq-widget .btn-check:checked + label .selection-marker {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary);
}

.iq-widget .btn-check:checked + label .selection-marker::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
}

.iq-service-box::after {
  display: none !important;
}

.iq-widget .btn-check:checked+label .iq-service-box::after {
  opacity: 1;
}

.bg-soft-primary { background-color: rgba(var(--bs-primary-rgb), 0.1); color: var(--bs-primary); }

.selected-summary-bar {
  flex: 0 0 auto;
  background: rgba(var(--bs-primary-rgb), 0.07);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.18);
  border-radius: 14px;
  padding: 12px 14px;
  margin-top: 1rem;
  animation: fade-in 0.25s ease-in-out both;
}

.selected-summary-bar .fw-bold {
  font-size: 0.8125rem;
  color: #16182b;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid rgba(var(--bs-primary-rgb), 0.25);
  color: #16182b;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 6px 4px 10px;
  border-radius: 999px;
}

.chip-remove {
  background: none;
  border: none;
  color: var(--bs-primary);
  line-height: 1;
  font-size: 16px;
  padding: 0 2px;
  cursor: pointer;
}
.category-filter-wrapper {
  overflow: hidden;
  margin-left: -15px;
  margin-right: -15px;
}

.category-scroll-container {
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding: 4px 15px 6px 15px;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.category-scroll-container::-webkit-scrollbar {
  display: none;
}

.category-card-item {
  flex: 0 0 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-card-image-box {
  width: 58px;
  height: 58px;
  background: #ffffff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #f2f4f7;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.category-card-item:hover .category-card-image-box {
  transform: scale(1.05);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
}

.category-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.category-placeholder-box, .category-placeholder-all {
  width: 100%;
  height: 100%;
  background: #fcfdfe;
  color: #adb5bd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-card-name {
  font-size: 11px;
  font-weight: 700;
  color: #4a5568;
  width: 100%;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-card-item.active .category-card-image-box {
  border-color: var(--bs-primary);
  background: rgba(var(--bs-primary-rgb), 0.06);
  box-shadow: 0 8px 25px rgba(var(--bs-primary-rgb), 0.15);
}

.category-card-item.active .category-card-name {
  color: var(--bs-primary);
}

.category-card-item.active .category-placeholder-all,
.category-card-item.active .category-placeholder-box {
  background: transparent;
  color: var(--bs-primary);
}

.search-icon-container {
  z-index: 5;
  pointer-events: none;
}

.search-wrapper .search-field.py-3 {
  /* py-3 is !important in bootstrap; the pinned filter block should stay short */
  padding-top: 0.65rem !important;
  padding-bottom: 0.65rem !important;
}

.search-field {
  background-color: #f6f7f9 !important;
  color: #16182b;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid transparent !important;
  transition: all 0.2s ease-in-out;
}

.search-field:focus {
  background-color: #ffffff !important;
  border-color: var(--bs-primary) !important;
  box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.08) !important;
}

.search-field::placeholder {
  color: #adb5bd;
  font-weight: 400;
}

.btn-back-to-list {
  background: none;
  border: none;
  color: #16182b;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 0;
  transition: opacity 0.2s;
}

.btn-back-to-list:hover {
  opacity: 0.7;
}

.detail-image-wrapper {
  height: 200px;
  overflow: hidden;
}

.detail-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-badge {
  background: #f8f9fa;
  color: #6c757d;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.detail-description p {
  font-size: 15px;
}

.detail-price {
  color: #FF1493; /* Premium Pink color for detail view price */
}

.btn-primary-pink {
  background-color: #FF1493 !important;
  border-color: #FF1493 !important;
  color: #ffffff !important;
  transition: all 0.3s ease;
}

.btn-primary-pink:hover {
  background-color: #db127d !important;
  border-color: #db127d !important;
  box-shadow: 0 4px 12px rgba(255, 20, 147, 0.2) !important;
}

/* Responsive Tweaks */
@media (max-width: 576px) {
  .category-filter-wrapper {
    margin-right: -10px;
  }

  .category-scroll-container {
    padding-right: 10px;
  }

  .detail-image-wrapper {
    max-height: 200px;
  }

  /* The card is only ~292px wide on a phone. With the photo on the left and a
     fixed price column on the right the name was left about 75px and broke
     mid-word. Dropping the price onto its own line under the name, and leaving
     only the selection dot in a column, roughly doubles the room the name gets. */
  .service-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto 22px;
    column-gap: 8px;
    row-gap: 2px;
    align-items: center;
  }

  /* Name on the first line across the full width; duration + details on the
     second line with the price at its right end; the selection dot spans both
     lines. Two lines per card instead of four. */
  .service-main,
  .service-meta-side {
    display: contents;
  }

  .service-main .service-name {
    grid-column: 1 / 3;
    grid-row: 1;
  }

  .service-main .service-sub {
    grid-column: 1;
    grid-row: 2;
    margin-top: 0;
  }

  .service-meta-side .service-price-primary {
    grid-column: 2;
    grid-row: 2;
    align-self: center;
  }

  .service-meta-side .selection-marker {
    grid-column: 3;
    grid-row: 1 / -1;
    align-self: center;
  }

  .service-image-wrapper {
    width: 56px;
    height: 56px;
    margin: 8px 6px 8px 8px;
  }

  /* The category strip is pinned above the list, so every pixel it takes is
     a pixel of list that never shows. */
  .category-card-item {
    flex-basis: 68px;
    gap: 6px;
  }

  .category-card-image-box {
    width: 46px;
    height: 46px;
    border-radius: 12px;
  }

  /* Left over from the desktop scrollbar gutter; on a phone it is 10px stolen
     from the widest thing on screen. */
  .card-list-data {
    padding-right: 0;
  }
}

/* Skeleton & Shimmer Effect */
.skeleton {
  background: #eee;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  animation: shine 1.5s linear infinite;
}

@keyframes shine {
  to {
    background-position-x: -200%;
  }
}

.skeleton-image-placeholder {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 10px;
}

.skeleton-line {
  height: 14px;
  border-radius: 4px;
}

.skeleton-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
}
</style>
